import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

type Props = {
  /** True when this quote is the sale's last one — removing it deletes the sale itself. */
  isLastQuote: boolean
  isSubmitting: boolean
  onConfirm: (reason: string) => void
  onCancel: () => void
}

/**
 * SOLY-ORDER-002 fiche 20 — confirmation for « retirer un devis de la vente ».
 *
 * The copy states what actually happens (fiche 18): the quote stops being accepted and stays
 * re-acceptable. It never promises a one-click undo — there is none any more, re-selling means
 * accepting the quote again. (The domain leaves the version `issued`, which the UI renders
 * « Généré », not « Envoyé » — do not name the badge in user-facing copy.)
 */
export function RemoveQuoteFromSaleDialog({ isLastQuote, isSubmitting, onConfirm, onCancel }: Props) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg"
        data-testid="remove-quote-from-sale-dialog"
      >
        <h2 className="text-lg font-semibold">Retirer ce devis de la vente ?</h2>

        <p className="mt-2 text-sm text-muted-foreground">
          Ses lignes quittent la vente et le devis n'est plus accepté. Vous pourrez l'accepter à
          nouveau plus tard : il reviendra alors dans la vente.
        </p>

        {isLastQuote ? (
          <p
            className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="remove-quote-last-warning"
          >
            C'est le seul devis de cette vente : la retirer <strong>supprime la vente</strong>. Le
            métré, la pose et les commandes fournisseurs en cours seront annulés.
          </p>
        ) : null}

        <div className="mt-4 space-y-2">
          <Label htmlFor="remove-quote-reason">Motif</Label>
          <Textarea
            id="remove-quote-reason"
            data-testid="remove-quote-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex : le client abandonne ce lot"
            maxLength={1000}
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting} data-testid="remove-quote-cancel-btn">
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(trimmed)}
            disabled={isSubmitting || trimmed.length === 0}
            data-testid="remove-quote-confirm-btn"
          >
            {isSubmitting ? 'Retrait…' : 'Retirer de la vente'}
          </Button>
        </div>
      </div>
    </div>
  )
}
